/**
 * Query-key factory (+ shared wire types) for the per-app Eddy-agent
 * allowlist picker (FRONT-1714, `AgentPickerDialog` / `CapabilitiesSection`'s
 * "Eddy agents" card). Mirrors `tool-access-keys.ts` line for line — see that
 * file's header for the full FRONT-1501 reasoning this reuses verbatim:
 * `GET /api/admin/apps/:slug/agent-access` takes no search parameter either
 * (it reads only `orgId` and `slug`), so the search term changes nothing
 * about the request and has no business in the query key. Filtering an
 * already-fetched catalogue is entirely client-side, and folding the term
 * into the key would just mint a fresh cache entry per keystroke, every one
 * resolving to the identical response.
 *
 * The thing that CAN silently go inert here is the client-side filter, not
 * the key — a filter that stopped reading its search argument would still
 * render, still look wired up, and just always show everything. That's what
 * `filterOfferableAgents`'s own tests guard (`agent-access-keys.test.ts`).
 */

/** The backend's hard cap on how many agents one app may allowlist (wire
 * contract: `PUT` 400s with `too_many_agents` past this). Surfaced in the
 * picker so an owner sees the limit before hitting the save error. */
export const MAX_APP_AGENTS = 10;

/** Trim + lowercase, once, so the filter sees a consistent value. */
export function normalizeAgentSearch(search?: string | null): string {
  return (search ?? "").trim().toLowerCase();
}

export const agentAccessKeys = {
  all: ["admin-app-agent-access"] as const,
  /** The invalidation target — also the only list, since there's no search-scoped variant. */
  lists: (slug: string) => [...agentAccessKeys.all, "list", slug] as const,
  /** The one list for this app. No search argument: see file header. */
  list: (slug: string) => [...agentAccessKeys.lists(slug)] as const,
} as const;

/**
 * The picker's client-side filter — the thing a missing/ignored search
 * argument would silently break, now that the term isn't in the query key.
 * Matches on agent name OR description, case-insensitively. Deliberately
 * does NOT match on `id`: an agent id is a UUID, meaningless for a person to
 * type or recognize (unlike `filterOfferableTools`, where a tool's `name`
 * IS the thing worth matching on).
 */
export function filterOfferableAgents<T extends { name: string; description: string }>(
  agents: T[],
  search?: string | null,
): T[] {
  const normalized = normalizeAgentSearch(search);
  if (!normalized) return agents;
  return agents.filter(
    (a) =>
      a.name.toLowerCase().includes(normalized) ||
      a.description.toLowerCase().includes(normalized),
  );
}

/**
 * One entry from the live offerable agent catalogue (`agent-access`'s
 * `agents`). `tools`, when present, is the agent's OWN tool list — shown on
 * the row so the owner ticks with their eyes open: an allowlisted agent
 * doesn't have a single visible risk level the way a tool does, it runs
 * whichever of these it decides to, as the app owner.
 */
export interface OfferableAppAgent {
  id: string;
  name: string;
  description: string;
  tools?: string[];
}

/**
 * `GET /api/admin/apps/:slug/agent-access` response shape.
 *
 * `stale`: stored allowlist ids that no longer resolve to a live, offerable
 * agent — same D6 reporting-not-pruning contract as `StaleAppTool` (the live
 * catalogue is the authority, a stale entry is reported rather than silently
 * dropped), but there is no per-entry reason to show here (unlike a tool, a
 * vanished agent carries no name/description to explain itself with) — just
 * the bare id.
 *
 * `catalogueDegraded`: true when the catalogue fetch itself failed upstream.
 * The route answers with an EMPTY `agents` list in that case, which — read
 * naively — would make every id in `selected` look stale. The dialog must
 * treat this as "couldn't check, nothing has changed" and never compute or
 * offer to prune a stale set while this is true.
 */
export interface AgentAccessResponse {
  agents: OfferableAppAgent[];
  selected: string[];
  stale: string[];
  scopeGranted: boolean;
  catalogueDegraded: boolean;
}

/** `PUT /api/admin/apps/:slug/agent-access` body. */
export interface AgentAccessSaveRequest {
  agentIds: string[];
}

/** `PUT /api/admin/apps/:slug/agent-access` success response. */
export interface AgentAccessSaveResponse {
  ok: true;
  selected: string[];
}
